import { DEMO_URL } from './schema.js';
import { DemoPackError, cardinalityError, statusTextFor, unsupported, invalidInput } from './errors.js';
import { parseRequest } from './parse.js';
import { countSql, deleteSql, insertSql, runQuery, selectSql, updateSql } from './sql.js';
export function createDemoFetch(engine) {
    return async (input, init) => {
        const request = new Request(input, init);
        const url = new URL(request.url);
        try {
            if (url.origin !== DEMO_URL) {
                throw unsupported(`DemoPack only accepts ${DEMO_URL} requests. Received: ${url.origin}`);
            }
            return await engine.runOperation(async (db) => {
                const parsed = parseRequest(request, engine.tables);
                if (parsed.method === 'GET' || parsed.method === 'HEAD') {
                    return executeRead(db, parsed);
                }
                if (parsed.method === 'POST') {
                    return executeWrite(db, parsed, await readJsonBody(request), 201);
                }
                if (parsed.method === 'PATCH') {
                    return executeWrite(db, parsed, await readJsonBody(request), 200);
                }
                if (parsed.method === 'DELETE') {
                    return executeWrite(db, parsed, null, 200);
                }
                throw unsupported(`DemoPack does not handle unsupported requests: ${parsed.method} ${url.pathname}`);
            });
        }
        catch (error) {
            if (error instanceof DemoPackError) {
                if (error.code === 'DMPACK_UNSUPPORTED') {
                    engine.recordUnsupported(error.message, url.pathname);
                }
                return error.toResponse();
            }
            return unsupported(error instanceof Error ? error.message : 'Unexpected DemoPack transport error.').toResponse();
        }
    };
}
async function executeRead(db, parsed) {
    let total = null;
    if (parsed.wantsCount) {
        const countQuery = countSql(parsed);
        const countResult = await runQuery(db, countQuery.text, countQuery.params);
        total = Number(countResult.rows[0]?.count ?? 0);
    }
    const dataQuery = selectSql(parsed);
    const data = await runQuery(db, dataQuery.text, dataQuery.params);
    const rows = data.rows;
    if (total !== null && (parsed.offset ?? 0) > 0 && (parsed.offset ?? 0) >= total) {
        const response = new DemoPackError('Requested range not satisfiable', {
            status: 416, code: 'PGRST103', details: `An offset of ${parsed.offset} was requested, but there are only ${total} rows.`,
        }).toResponse();
        response.headers.set('Content-Range', `*/${total}`);
        return parsed.method === 'HEAD' ? new Response(null, { status: 416, headers: response.headers }) : response;
    }
    const status = total !== null && rows.length < total ? 206 : 200;
    if (parsed.singleObject) {
        if (rows.length !== 1) {
            return cardinalityError(rows.length).toResponse();
        }
        return parsed.method === 'HEAD'
            ? new Response(null, { status, headers: headersFor(null, contentRange(parsed, rows.length, total)) })
            : jsonResponse(status, rows[0], contentRange(parsed, rows.length, total));
    }
    if (parsed.method === 'HEAD') {
        return new Response(null, {
            status,
            statusText: statusTextFor(status),
            headers: headersFor(null, contentRange(parsed, rows.length, total)),
        });
    }
    return jsonResponse(status, rows, contentRange(parsed, rows.length, total));
}
async function executeWrite(db, parsed, body, successStatus) {
    const statement = parsed.method === 'POST'
        ? insertSql(parsed, body)
        : parsed.method === 'PATCH'
            ? updateSql(parsed, body)
            : deleteSql(parsed);
    await runQuery(db, 'BEGIN', []);
    try {
        let total = null;
        if (parsed.wantsCount && parsed.method !== 'POST') {
            const countQuery = countSql(parsed);
            const countResult = await runQuery(db, countQuery.text, countQuery.params);
            total = Number(countResult.rows[0]?.count ?? 0);
        }
        const result = await runQuery(db, statement.text, statement.params);
        if (parsed.method === 'POST' && parsed.wantsCount) {
            total = result.rows.length || result.rowCount;
        }
        if (parsed.returnRepresentation && parsed.singleObject && result.rows.length !== 1) {
            throw cardinalityError(result.rows.length);
        }
        await runQuery(db, 'COMMIT', []);
        if (!parsed.returnRepresentation) {
            const status = parsed.method === 'POST' ? 201 : 204;
            return new Response(null, {
                status,
                statusText: statusTextFor(status),
                headers: headersFor(null, contentRange(parsed, total ?? result.rowCount, total)),
            });
        }
        if (parsed.singleObject) {
            if (result.rows.length !== 1) {
                return cardinalityError(result.rows.length).toResponse();
            }
            return jsonResponse(successStatus, result.rows[0], contentRange(parsed, 1, total));
        }
        return jsonResponse(successStatus, result.rows, contentRange(parsed, result.rows.length, total));
    }
    catch (error) {
        try {
            await runQuery(db, 'ROLLBACK', []);
        }
        catch {
            // The original engine error is more useful than a rollback failure.
        }
        throw error;
    }
}
async function readJsonBody(request) {
    const text = await request.text();
    if (new globalThis.TextEncoder().encode(text).byteLength > 1048576)
        throw invalidInput('Request body exceeds the 1 MiB limit.');
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    }
    catch {
        throw unsupported('Request body must be valid JSON.');
    }
}
function jsonResponse(status, body, range) {
    return new Response(JSON.stringify(body), {
        status,
        statusText: statusTextFor(status),
        headers: headersFor('application/json', range),
    });
}
function headersFor(contentType, range) {
    const headers = new Headers();
    if (contentType) {
        headers.set('Content-Type', contentType);
    }
    if (range) {
        headers.set('Content-Range', range);
        headers.set('Content-Range'.toLowerCase(), range);
    }
    return headers;
}
function contentRange(parsed, returnedCount, total) {
    if (total === null && returnedCount === 0) {
        return parsed.wantsCount ? '*/0' : null;
    }
    if (total === null) {
        const start = parsed.offset ?? 0;
        const end = start + Math.max(returnedCount - 1, 0);
        return returnedCount === 0 ? '*/*' : `${start}-${end}/*`;
    }
    if (returnedCount === 0) {
        return `*/${total}`;
    }
    const start = parsed.offset ?? 0;
    const end = start + returnedCount - 1;
    return `${start}-${end}/${total}`;
}
